import { useCallback, useEffect, useRef, useState } from 'react';

import {
  faMicrophone,
  faMicrophoneSlash,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { faChevronDown } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@repo/videa-components/src/index';
import { useAtom, useAtomValue } from 'jotai';

import { createDebugLogger, useLatest } from '@videa/utils';

import {
  hasAudioActivityAtom,
  isRecordingSessionActiveAtom,
  previewAudioStreamAtom,
  recorderStatusAtom,
} from '../../atoms/recording';
import {
  isDeviceSelectionOpenAtom,
  isRecorderSheetOpenAtom,
} from '../../atoms/ui';
import {
  buildAudioConstraints,
  findApprovedDevice,
} from '../../contexts/microphone';
import { useMicrophoneContext } from '../../contexts/MicrophoneContext';

const debug = createDebugLogger('MicrophoneSelector');

/** Maximum number of consecutive getUserMedia failures before giving up. */
const MAX_MONITORING_RETRIES = 3;

interface MicrophoneSelectorProps {
  /** Direction the dropdown menu should open. Defaults to 'down'. */
  dropdownDirection?: 'up' | 'down';
  onMonitoringStreamChange?: (stream: MediaStream | null) => void;
  /**
   * Callback fired when checking for a preferred microphone.
   * Called with the microphone name if one was found and selected, or null if not found.
   */
  onPreferredMicrophoneCheck?: (microphoneName: string | null) => void;
}

export const MicrophoneSelector = ({
  dropdownDirection = 'down',
  onMonitoringStreamChange,
  onPreferredMicrophoneCheck,
}: MicrophoneSelectorProps) => {
  const [isOpen, setIsOpen] = useAtom(isDeviceSelectionOpenAtom);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [monitoringStream, setMonitoringStream] = useState<MediaStream | null>(
    null,
  );
  const [monitoringError, setMonitoringError] = useState<string | null>(null);
  const isSheetOpen = useAtomValue(isRecorderSheetOpenAtom);
  const recorderStatus = useAtomValue(recorderStatusAtom);
  const isRecordingActive = useAtomValue(isRecordingSessionActiveAtom);

  // Use same "active" logic as DeviceStatusIndicator: stream + audio activity
  const previewAudioStream = useAtomValue(previewAudioStreamAtom);
  const hasAudioActivity = useAtomValue(hasAudioActivityAtom);
  const streamForStatus = monitoringStream || previewAudioStream;
  const isMicActive = streamForStatus?.active === true && hasAudioActivity;

  const {
    devices: availableDevices,
    selectedDeviceId,
    selectedDevice,
    hasPermission: hasMicrophonePermission,
    isLoading,
    error,
    selectDevice,
  } = useMicrophoneContext();

  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const monitoringStreamRef = useRef<MediaStream | null>(null);
  const prevDeviceIdRef = useRef<string | null>(null);
  const retryCountRef = useRef(0);

  // This avoids stale closures when the callback changes after getUserMedia is assigned
  const onMonitoringStreamChangeRef = useLatest(onMonitoringStreamChange);

  // ---- Preferred-mic analytics callback ----
  // This is the sole owner of onPreferredMicrophoneCheck.
  // The callback lives here (not in useMicrophone / MicrophoneProvider) because
  // it's an analytics concern specific to the UI that renders the selector.
  const onPreferredMicrophoneCheckRef = useLatest(onPreferredMicrophoneCheck);
  const prevDeviceIdsRef = useRef<string>('');

  useEffect(() => {
    // Build a stable fingerprint so we detect actual device changes,
    // not just length changes (which would miss a swap of equal count).
    const deviceIds = availableDevices
      .map((d) => d.deviceId)
      .sort()
      .join(',');
    if (deviceIds === prevDeviceIdsRef.current) return;
    prevDeviceIdsRef.current = deviceIds;

    const approvedDevice = findApprovedDevice(availableDevices);
    onPreferredMicrophoneCheckRef.current?.(approvedDevice?.label ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useLatest ref is stable
  }, [availableDevices]);

  const handleDeviceSelect = (deviceId: string) => {
    selectDevice(deviceId);
    setIsOpen(false);
  };

  // Close dropdown when recording starts
  useEffect(() => {
    if (isRecordingActive) {
      setIsOpen(false);
    }
  }, [isRecordingActive, setIsOpen]);

  const stopMicrophoneMonitoring = useCallback(() => {
    setIsMonitoring(false);

    const stream = monitoringStreamRef.current;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setMonitoringStream(null);
      onMonitoringStreamChangeRef.current?.(null);
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useLatest ref is stable
  }, []);

  const startMicrophoneMonitoring = useCallback(
    async (deviceId: string | null) => {
      try {
        setIsMonitoring(true);
        setMonitoringError(null);

        // Use the shared buildAudioConstraints helper so monitoring and
        // recording always target the exact same device. See that function's
        // JSDoc for the rationale behind 'exact' over 'ideal'.
        const audioTrackConstraints = buildAudioConstraints(deviceId);
        const constraints: MediaStreamConstraints = {
          audio: audioTrackConstraints,
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);

        // Verify the browser actually gave us the requested device.
        // With 'exact' constraint this should never mismatch (the browser should
        // throw OverconstrainedError instead of falling back), but we check
        // defensively in case a browser doesn't honor 'exact' correctly.
        const actualTrack = stream.getAudioTracks()[0];
        if (actualTrack && deviceId) {
          const actualDeviceId = actualTrack.getSettings?.()?.deviceId;
          if (actualDeviceId && actualDeviceId !== deviceId) {
            debug.warn(
              `Monitoring device mismatch: requested=${deviceId}, got=${actualDeviceId} (${actualTrack.label}). Rejecting mismatched stream.`,
            );
            // Stop the wrong stream - don't expose it to VoiceCard
            stream.getTracks().forEach((track) => track.stop());
            // Exhaust retries so the main effect doesn't re-trigger and loop
            retryCountRef.current = MAX_MONITORING_RETRIES;
            setIsMonitoring(false);
            setMonitoringError(
              'The selected microphone could not be accessed. It may be disconnected or in use by another application.',
            );
            return;
          }
          debug.log(
            `Monitoring started on device: ${actualTrack.label} (${actualDeviceId})`,
          );
        }

        // Stop any existing stream before assigning the new one
        // This prevents orphaned streams when multiple calls race
        const previousStream = monitoringStreamRef.current;
        if (previousStream && previousStream !== stream) {
          previousStream.getTracks().forEach((track) => track.stop());
        }

        monitoringStreamRef.current = stream;
        setMonitoringStream(stream);
        onMonitoringStreamChangeRef.current?.(stream);

        // Reset retry count on success
        retryCountRef.current = 0;

        // Set up audio analysis for the VoiceCard
        const audioContext = new (window.AudioContext ||
          (window as any).webkitAudioContext)();
        const analyser = audioContext.createAnalyser();
        const source = audioContext.createMediaStreamSource(stream);

        analyser.fftSize = 256;
        source.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;
      } catch (err) {
        debug.error('Failed to start microphone monitoring:', err);
        setIsMonitoring(false);

        // Track retries and give up after MAX_MONITORING_RETRIES
        retryCountRef.current += 1;
        if (retryCountRef.current >= MAX_MONITORING_RETRIES) {
          const errorMessage =
            err instanceof Error ? err.message : 'Unknown error';
          debug.error(
            `Giving up after ${MAX_MONITORING_RETRIES} monitoring attempts: ${errorMessage}`,
          );
          setMonitoringError(
            'Could not access microphone. Please check your device and try again.',
          );
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useLatest ref is stable
    [],
  );

  // Keep refs in sync with their values
  useEffect(() => {
    monitoringStreamRef.current = monitoringStream;
  }, [monitoringStream]);

  // Main effect: manage monitoring based on sheet state, device selection, recording status, and permission
  useEffect(() => {
    // Only allow monitoring when recorder is idle (not recording, paused, stopped, etc.)
    const canMonitor = recorderStatus === 'idle';
    const deviceChanged =
      prevDeviceIdRef.current !== null &&
      prevDeviceIdRef.current !== selectedDeviceId;

    // Reset retry count when device changes (user explicitly picked a different mic)
    if (deviceChanged) {
      retryCountRef.current = 0;
      setMonitoringError(null);
    }

    // Stop monitoring if: sheet closed, not in idle state, or no permission
    if (!isSheetOpen || !canMonitor || !hasMicrophonePermission) {
      if (isMonitoring) {
        stopMicrophoneMonitoring();
      }
    }
    // Start/restart monitoring if: sheet open, idle state, device selected, not already monitoring, has permission
    // Also respect the retry limit
    else if (
      selectedDeviceId &&
      !isMonitoring &&
      hasMicrophonePermission &&
      retryCountRef.current < MAX_MONITORING_RETRIES
    ) {
      startMicrophoneMonitoring(selectedDeviceId);
    } else if (deviceChanged && isMonitoring) {
      // Device changed while monitoring - restart with new device
      stopMicrophoneMonitoring();
      startMicrophoneMonitoring(selectedDeviceId);
    }

    prevDeviceIdRef.current = selectedDeviceId;
  }, [
    isSheetOpen,
    selectedDeviceId,
    recorderStatus,
    isMonitoring,
    hasMicrophonePermission,
    startMicrophoneMonitoring,
    stopMicrophoneMonitoring,
  ]);

  // Safety net: clean up stream AND AudioContext if they shouldn't exist
  useEffect(() => {
    const canMonitor = recorderStatus === 'idle';
    const shouldNotHaveStream = !isSheetOpen || !canMonitor;

    if (shouldNotHaveStream && monitoringStream) {
      monitoringStream.getTracks().forEach((track) => track.stop());
      setMonitoringStream(null);
      onMonitoringStreamChangeRef.current?.(null);

      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      analyserRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useLatest ref is stable
  }, [isSheetOpen, recorderStatus, monitoringStream]);

  // Detect when the monitoring stream's track ends (e.g., permission revoked, device disconnected)
  useEffect(() => {
    if (!monitoringStream) return;

    const audioTrack = monitoringStream.getAudioTracks()[0];
    if (!audioTrack) return;

    if (audioTrack.readyState === 'ended') {
      stopMicrophoneMonitoring();
      return;
    }

    const handleTrackEnded = () => {
      stopMicrophoneMonitoring();
    };

    audioTrack.addEventListener('ended', handleTrackEnded);

    return () => {
      audioTrack.removeEventListener('ended', handleTrackEnded);
    };
  }, [monitoringStream, stopMicrophoneMonitoring]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopMicrophoneMonitoring();
    };
  }, [stopMicrophoneMonitoring]);

  if (error) {
    return (
      <div className="vai-flex vai-items-center vai-gap-2 vai-p-2 vai-bg-red-50 vai-border vai-border-red-200 vai-rounded-md">
        <FontAwesomeIcon
          icon={faMicrophone}
          className="vai-w-4 vai-h-4 vai-text-red-500"
        />
        <span className="vai-text-sm vai-text-red-700">{error.message}</span>
      </div>
    );
  }

  // Show monitoring error with retry option
  if (monitoringError) {
    return (
      <div className="vai-flex vai-items-center vai-gap-2 vai-p-2 vai-bg-amber-50 vai-border vai-border-amber-200 vai-rounded-md">
        <FontAwesomeIcon
          icon={faMicrophone}
          className="vai-w-4 vai-h-4 vai-text-amber-500"
        />
        <span className="vai-text-sm vai-text-amber-700">
          {monitoringError}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            retryCountRef.current = 0;
            setMonitoringError(null);
            if (selectedDeviceId) {
              startMicrophoneMonitoring(selectedDeviceId);
            }
          }}
          className="vai-ml-auto vai-text-xs"
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="vai-space-y-3">
      <div className="vai-relative">
        <Button
          variant="outline"
          onClick={() => setIsOpen(!isOpen)}
          disabled={
            isLoading || availableDevices.length === 0 || isRecordingActive
          }
          className="vai-flex vai-items-center vai-gap-2 vai-w-full vai-justify-between vai-font-normal"
          data-testid="microphone-selector-button"
        >
          <div className="vai-flex vai-items-center vai-gap-2 vai-min-w-0 vai-flex-1">
            {isMicActive ? (
              <FontAwesomeIcon
                icon={faMicrophone}
                className="vai-w-4 vai-h-4 vai-flex-shrink-0 vai-text-leaf-700"
              />
            ) : (
              <FontAwesomeIcon
                icon={faMicrophoneSlash}
                className="vai-w-4 vai-h-4 vai-flex-shrink-0 vai-text-red-600"
              />
            )}
            <span className="vai-text-sm vai-truncate vai-min-w-0">
              {!hasMicrophonePermission
                ? 'None'
                : isLoading
                ? 'Loading microphones...'
                : selectedDevice?.label || 'Select microphone'}
            </span>
          </div>
          <FontAwesomeIcon
            icon={faChevronDown}
            className={`vai-w-3 vai-h-3 vai-opacity-50 vai-shrink-0 vai-transition-transform ${
              isOpen ? 'vai-rotate-180' : ''
            }`}
          />
        </Button>

        {isOpen && (
          <div
            className={`vai-absolute vai-left-0 vai-right-0 vai-min-w-[220px] vai-bg-white vai-border vai-border-gray-200 vai-rounded-md vai-shadow-lg vai-z-50 vai-max-h-60 vai-overflow-y-auto ${
              dropdownDirection === 'up'
                ? 'vai-bottom-full vai-mb-1'
                : 'vai-top-full vai-mt-1'
            }`}
          >
            {!hasMicrophonePermission ? (
              <div className="vai-p-3 vai-text-sm vai-text-gray-500 vai-text-center vai-whitespace-nowrap">
                Microphone permission required
              </div>
            ) : availableDevices.length === 0 ? (
              <div className="vai-p-3 vai-text-sm vai-text-gray-500 vai-text-center vai-whitespace-nowrap">
                No microphones found
              </div>
            ) : (
              <>
                <div className="vai-p-2 vai-border-b vai-border-gray-200">
                  <span className="vai-text-sm">Select Microphone</span>
                </div>
                {availableDevices.map((device) => {
                  const isSelected = device.deviceId === selectedDeviceId;
                  const isSelectedAndConnected = isSelected && isMicActive;
                  return (
                    <button
                      key={device.deviceId}
                      onClick={() => handleDeviceSelect(device.deviceId)}
                      className={`vai-w-full vai-px-3 vai-py-2 vai-text-left vai-text-sm hover:vai-bg-gray-50 vai-flex vai-items-center vai-gap-2 ${
                        isSelected
                          ? 'vai-bg-blue-50 vai-text-blue-700'
                          : 'vai-text-gray-700'
                      }`}
                    >
                      {isSelectedAndConnected ? (
                        <FontAwesomeIcon
                          icon={faMicrophone}
                          className="vai-w-4 vai-h-4 vai-flex-shrink-0 vai-text-leaf-700"
                        />
                      ) : isSelected ? (
                        <FontAwesomeIcon
                          icon={faMicrophoneSlash}
                          className="vai-w-4 vai-h-4 vai-flex-shrink-0 vai-text-red-600"
                        />
                      ) : (
                        <FontAwesomeIcon
                          icon={faMicrophone}
                          className="vai-w-4 vai-h-4 vai-flex-shrink-0"
                        />
                      )}
                      <span className="vai-truncate">{device.label}</span>
                      {device.deviceId === selectedDeviceId && (
                        <div className="vai-w-2 vai-h-2 vai-min-w-2 vai-min-h-2 vai-flex-shrink-0 vai-bg-blue-500 vai-rounded-full vai-ml-auto" />
                      )}
                    </button>
                  );
                })}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
